import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Webcam from "react-webcam";
import { motion, AnimatePresence } from "framer-motion";
import { X, RotateCcw, Check, ImagePlus, Aperture } from "lucide-react";
import { toast } from "sonner";
import {
  compressDataUrl,
  uploadSnap,
  compareSnaps,
  createRecipes,
} from "@/lib/api";
import {
  savePendingBefore,
  getPendingBefore,
  clearPendingBefore,
  getDisplayName,
} from "@/lib/device";
import { LOADING_LINES, CAPTURE_TIPS, pick } from "@/lib/copy";
import { haptic } from "@/lib/haptics";

export default function Capture() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const role = params.get("role") === "after" ? "after" : "before";
  const fridgeCode = params.get("fridge") || null;
  const mode = params.get("mode");
  const diet = params.get("diet") || "any";
  const effort = params.get("effort") || "quick";
  const cuisine = params.get("cuisine") || "any";

  const webcamRef = useRef(null);
  const fileRef = useRef(null);
  const [captured, setCaptured] = useState(null);
  const [camReady, setCamReady] = useState(false);
  const [camError, setCamError] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loadingLine, setLoadingLine] = useState(LOADING_LINES[0]);

  const pending = useMemo(() => (role === "after" ? getPendingBefore() : null), [role]);
  const tip = useMemo(() => pick(CAPTURE_TIPS), []);

  useEffect(() => {
    if (role === "after" && !pending) {
      toast("No 'before' shot found. Start with the before.");
      navigate("/");
    }
  }, [role, pending, navigate]);

  useEffect(() => {
    if (!processing) return;
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % LOADING_LINES.length;
      setLoadingLine(LOADING_LINES[i]);
    }, 1600);
    return () => clearInterval(t);
  }, [processing]);

  const shoot = useCallback(async () => {
    haptic(15);
    const shot = webcamRef.current?.getScreenshot();
    if (!shot) {
      toast("Camera's shy. Try the upload button.");
      return;
    }
    const compressed = await compressDataUrl(shot);
    setCaptured(compressed);
  }, []);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const compressed = await compressDataUrl(reader.result);
      setCaptured(compressed);
    };
    reader.readAsDataURL(file);
  };

  const confirm = async () => {
    if (!captured) return;
    setProcessing(true);
    try {
      if (mode === "cook") {
        const snap = await uploadSnap(captured, "cook");
        const rec = await createRecipes(snap.id, diet, effort, cuisine);
        navigate(`/recipes/${rec.id}`, { state: rec });
        return;
      }
      const snap = await uploadSnap(captured, role);
      if (role === "before") {
        savePendingBefore({
          id: snap.id,
          dataUrl: captured,
          fridgeCode: fridgeCode || null,
          memberName: fridgeCode ? getDisplayName() : null,
        });
        toast("Before locked in.");
        navigate("/");
      } else {
        const before = getPendingBefore();
        const comp = await compareSnaps(before.id, snap.id, {
          fridge_code: before.fridgeCode,
          member_name: before.memberName,
        });
        clearPendingBefore();
        navigate(`/reveal/${comp.id}`, { state: comp });
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error(err);
      toast("Something jammed. Give it another go.");
      setProcessing(false);
    }
  };

  return (
    <div className="app-shell relative min-h-[100dvh] bg-[#111] text-white overflow-hidden" data-testid="capture-screen">
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-5 pt-[calc(env(safe-area-inset-top)+1.25rem)]">
        <button
          data-testid="capture-close-btn"
          onClick={() => navigate("/")}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <span className="text-xs uppercase tracking-[0.28em] font-semibold bg-black/40 backdrop-blur px-3 py-2 rounded-full">
          {mode === "cook" ? "Cook" : role === "before" ? "Before" : "After"}
        </span>
        <div className="w-10" />
      </div>

      {/* Live camera / preview */}
      <div className="absolute inset-0 z-0">
        {captured ? (
          <img src={captured} alt="captured" className="w-full h-full object-cover" />
        ) : camError ? (
          <div className="w-full h-full flex flex-col items-center justify-center px-8 text-center gap-4">
            <Aperture className="w-10 h-10 opacity-60" strokeWidth={1.2} />
            <p className="font-serif text-2xl">The camera won't cooperate.</p>
            <p className="text-sm text-white/60">Grant camera access, or upload a photo from your library.</p>
          </div>
        ) : (
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            screenshotQuality={0.85}
            forceScreenshotSourceSize
            videoConstraints={{ facingMode: "environment" }}
            onUserMedia={() => setCamReady(true)}
            onUserMediaError={() => setCamError(true)}
            className="w-full h-full object-cover"
          />
        )}

        {/* Ghost overlay of the before shot */}
        {role === "after" && pending && !captured && (
          <img
            src={pending.dataUrl}
            alt="ghost"
            className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none mix-blend-luminosity"
          />
        )}
      </div>

      {/* Viewfinder frame */}
      {!captured && (
        <div className="absolute inset-0 z-10 pointer-events-none flex items-center justify-center px-8">
          <div className="w-full aspect-[3/4] max-h-[62dvh] rounded-lg border-[1.5px] border-white/70 relative">
            <Corner className="top-0 left-0" />
            <Corner className="top-0 right-0 rotate-90" />
            <Corner className="bottom-0 right-0 rotate-180" />
            <Corner className="bottom-0 left-0 -rotate-90" />
          </div>
        </div>
      )}

      {/* Hint */}
      {!captured && (
        <div className="absolute z-20 left-0 right-0 text-center px-8" style={{ bottom: "10.5rem" }}>
          <p className="text-sm text-white/80 bg-black/30 backdrop-blur inline-block px-3 py-1.5 rounded-full">
            {role === "after" ? "Line it up with the ghost, then shoot." : tip}
          </p>
        </div>
      )}

      {/* Controls */}
      <div className="absolute bottom-0 inset-x-0 z-20 pb-[calc(env(safe-area-inset-bottom)+2.5rem)] pt-8 bg-gradient-to-t from-black/70 to-transparent">
        {captured ? (
          <div className="flex items-center justify-center gap-4 px-8">
            <button
              data-testid="retake-btn"
              onClick={() => setCaptured(null)}
              className="flex-1 rounded-sm border border-white/40 py-4 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <RotateCcw className="w-5 h-5" strokeWidth={1.5} /> Retake
            </button>
            <button
              data-testid="confirm-btn"
              onClick={confirm}
              className="flex-1 rounded-sm bg-white text-[#111] py-4 font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Check className="w-5 h-5" strokeWidth={2} />
              {mode === "cook" ? "Get recipes" : role === "before" ? "Use this" : "Compare"}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-10">
            <button
              data-testid="upload-fallback-btn"
              onClick={() => fileRef.current?.click()}
              className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center"
            >
              <ImagePlus className="w-6 h-6" strokeWidth={1.5} />
            </button>
            <button
              data-testid="shutter-btn"
              onClick={shoot}
              disabled={!camReady && !camError}
              className="w-20 h-20 rounded-full bg-white flex items-center justify-center active:scale-95 transition-transform disabled:opacity-40"
            >
              <span className="w-16 h-16 rounded-full border-[3px] border-[#111]" />
            </button>
            <div className="w-12" />
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPickFile}
          className="hidden"
          data-testid="file-input"
        />
      </div>

      {/* Processing overlay */}
      <AnimatePresence>
        {processing && (
          <motion.div
            className="absolute inset-0 z-40 bg-[#eef5f6] text-[#16242a] flex flex-col items-center justify-center px-10 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="processing-overlay"
          >
            <div className="w-10 h-10 rounded-full border-2 border-[#1a1a1a] border-t-transparent spin-slow" />
            <AnimatePresence mode="wait">
              <motion.p
                key={loadingLine}
                className="font-serif text-3xl mt-8"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4 }}
              >
                {loadingLine}
              </motion.p>
            </AnimatePresence>
            <p className="text-sm text-[#5c5c5c] mt-4">The fridge is being reviewed.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Corner({ className }) {
  return (
    <span className={`absolute w-5 h-5 border-t-2 border-l-2 border-white ${className}`} />
  );
}
